import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, filter, forkJoin, map, Observable } from 'rxjs';
import { ReferenceService } from '../services/reference.service';
import { IReferenceData } from '../shared/interfaces/form/reference-data.model';

@Injectable({
  providedIn: 'root',
})
export class ReferenceStore {
  private cache: Map<string, BehaviorSubject<IReferenceData[] | null>> =
    new Map<string, BehaviorSubject<IReferenceData[] | null>>();

  constructor(private referenceService: ReferenceService) {}

  private load(ddName: string): Observable<IReferenceData[]> {
    if (!this.cache.has(ddName)) {
      const subject: BehaviorSubject<IReferenceData[] | null> =
        new BehaviorSubject<IReferenceData[] | null>(null);
      this.cache.set(ddName, subject);

      this.referenceService.getDropDownValues(ddName).subscribe({
        next: (data: IReferenceData[]) => subject.next(data),
        error: (err: HttpErrorResponse) => subject.error(err),
      });
    }

    return this.cache
      .get(ddName)!
      .asObservable()
      .pipe(filter((v: IReferenceData[]) => v !== null));
  }

  get otherImpairment$(): Observable<IReferenceData[]> {
    return this.load('OtherImpairment');
  }

  get disabilityClass$(): Observable<IReferenceData[]> {
    return this.load('DisabilityClass');
  }

  get state$(): Observable<IReferenceData[]> {
    return this.load('State');
  }

  get language$(): Observable<IReferenceData[]> {
    return this.load('Language');
  }

  get relationship$(): Observable<IReferenceData[]> {
    return this.load('Relationship');
  }

  get personCategory$(): Observable<IReferenceData[]> {
    return this.load('PersonCategory');
  }

  get waitTime$(): Observable<IReferenceData[]> {
    return this.load('WaitTime');
  }

  get district$(): Observable<IReferenceData[]> {
    return this.load('District');
  }

  get borough$(): Observable<IReferenceData[]> {
    return this.load('Borough');
  }

  get decisionReasons$(): Observable<IReferenceData[]> {
    return this.load('DecisionReasons');
  }

  get evaluationType$(): Observable<IReferenceData[]> {
    return this.load('EvaluationType');
  }

  get evaluationStatus$(): Observable<IReferenceData[]> {
    return this.load('EvalStatus');
  }

  get placementRecType$(): Observable<IReferenceData[]> {
    return this.load('PlacementRecType');
  }

  get dashboardActivity$(): Observable<IReferenceData[]> {
    return this.load('DashboardActivity');
  }

  get caseStage$(): Observable<IReferenceData[]> {
    return this.load('CaseStage');
  }

  get evaluatorType$(): Observable<IReferenceData[]> {
    return this.load('EvaluatorType');
  }

  get documentType$(): Observable<IReferenceData[]> {
    return this.load('DocumentType');
  }

  get eiChildStatus$(): Observable<IReferenceData[]> {
    return this.load('EIChildStatus');
  }

  get documentStatus$(): Observable<IReferenceData[]> {
    return this.load('DocumentStatus');
  }

  get fosterAgency$(): Observable<IReferenceData[]> {
    return this.load('FosterAgency');
  }

  get eiAutoEligibleCond$(): Observable<IReferenceData[]> {
    return this.load('EIAutoEligibleCond');
  }

  get discipline$(): Observable<IReferenceData[]> {
    return this.load('Discipline');
  }

  get intensity$(): Observable<IReferenceData[]> {
    return this.load('Intensity');
  }

  get groupType$(): Observable<IReferenceData[]> {
    return this.load('GroupType');
  }

  get frequencyInterval$(): Observable<IReferenceData[]> {
    return this.load('FrequencyInterval');
  }

  get asstTechDeviceType$(): Observable<IReferenceData[]> {
    return this.load('AsstTechDeviceType');
  }

  get asstTechDuration$(): Observable<IReferenceData[]> {
    return this.load('AsstTechDuration');
  }

  get locations$(): Observable<IReferenceData[]> {
    return this.load('Locations');
  }

  get gmfcsLevel$(): Observable<IReferenceData[]> {
    return this.load('GMFCSLevel');
  }

  get rServiceType$(): Observable<IReferenceData[]> {
    return this.load('RServiceType');
  }

  get ethnicity$(): Observable<IReferenceData[]> {
    return this.load('Ethnicity');
  }

  get iepSection$(): Observable<IReferenceData[]> {
    return this.load('IEPSection');
  }

  get cspOptCons$(): Observable<IReferenceData[]> {
    return this.load('CSPOptCons');
  }

  get assessUsed$(): Observable<IReferenceData[]> {
    return this.load('AssessUsed');
  }

  get compServices$(): Observable<IReferenceData[]> {
    return this.load('CompServices');
  }

  get subject$(): Observable<IReferenceData[]> {
    return this.load('Subject');
  }

  get svcProvidedBy$(): Observable<IReferenceData[]> {
    return this.load('SvcProvidedBy');
  }

  get pServiceType$(): Observable<IReferenceData[]> {
    return this.load('PServiceType');
  }

  get svcLocation$(): Observable<IReferenceData[]> {
    return this.load('SvcLocation');
  }

  get classroomSetting$(): Observable<IReferenceData[]> {
    return this.load('ClassroomSetting');
  }

  get frequencyNumber$(): Observable<IReferenceData[]> {
    return this.load('FrequencyNumber');
  }

  get otherConsid$(): Observable<IReferenceData[]> {
    return this.load('OtherConsid');
  }

  get hoursDuration$(): Observable<IReferenceData[]> {
    return this.load('HoursDuration');
  }

  get suppAidsType$(): Observable<IReferenceData[]> {
    return this.load('SuppAidsType');
  }

  get paraProType$(): Observable<IReferenceData[]> {
    return this.load('ParaProType');
  }

  get healthOptions$(): Observable<IReferenceData[]> {
    return this.load('HealthOptions');
  }

  get intervalDuration$(): Observable<IReferenceData[]> {
    return this.load('IntervalDuration');
  }

  get paraDuration$(): Observable<IReferenceData[]> {
    return this.load('ParaDuration');
  }

  get groupSize$(): Observable<IReferenceData[]> {
    return this.load('GroupSize');
  }

  get ratioOptions$(): Observable<IReferenceData[]> {
    return this.load('RatioOptions');
  }

  get preSchoolRatio$(): Observable<IReferenceData[]> {
    return this.load('PreSchoolRatio');
  }

  get ratio$(): Observable<IReferenceData[]> {
    return this.load('Ratio');
  }

  get deferredPlacements$(): Observable<IReferenceData[]> {
    return this.load('DeferredPlacements');
  }

  get dayType$(): Observable<IReferenceData[]> {
    return this.load('DayType');
  }

  get referralRequestGrades$(): Observable<IReferenceData[]> {
    return this.load('ReferralRequestGrades');
  }

  get testAccommodation$(): Observable<IReferenceData[]> {
    return this.load('TestAccommodation');
  }

  get transpoRecommend$(): Observable<IReferenceData[]> {
    return this.load('TranspoRecommend');
  }

  get ambulatoryCodes$(): Observable<IReferenceData[]> {
    return this.load('AmbulatoryCodes');
  }

  get ineligDetReasons$(): Observable<IReferenceData[]> {
    return this.load('IneligDetReasons');
  }

  get progressReport$(): Observable<IReferenceData[]> {
    return this.load('ProgressReport');
  }

  get measurementMethod$(): Observable<IReferenceData[]> {
    return this.load('MeasurementMethod');
  }

  get progressReasons$(): Observable<IReferenceData[]> {
    return this.load('ProgressReasons');
  }

  get progressStatus$(): Observable<IReferenceData[]> {
    return this.load('ProgressStatus');
  }

  get preSchoolType$(): Observable<IReferenceData[]> {
    return this.load('PreSchoolType');
  }

  get schoolYear$(): Observable<IReferenceData[]> {
    return this.load('SchoolYear');
  }

  get caseCloseReason$(): Observable<IReferenceData[]> {
    return this.load('CaseCloseReason');
  }

  get interventionType$(): Observable<IReferenceData[]> {
    return this.load('InterventionType');
  }

  get redAlert$(): Observable<IReferenceData[]> {
    return this.load('RedAlert');
  }

  get grade$(): Observable<IReferenceData[]> {
    return this.load('Grade');
  }

  get sex$(): Observable<IReferenceData[]> {
    return this.load('Sex');
  }

  get dbn$(): Observable<IReferenceData[]> {
    return this.load('Dbn');
  }

  get extReq$(): Observable<IReferenceData[]> {
    return this.load('ExtReq');
  }

  get assessmentRejection$(): Observable<IReferenceData[]> {
    return this.load('AssessmentRejection');
  }

  get dischargeReason$(): Observable<IReferenceData[]> {
    return this.load('DischargeReason');
  }

  get reasonForDelay$(): Observable<IReferenceData[]> {
    return this.load('ReasonForDelay');
  }

  get engagementstatus$(): Observable<IReferenceData[]> {
    return this.load('Engagementstatus');
  }

  get outreachMethod$(): Observable<IReferenceData[]> {
    return this.load('OutreachMethod');
  }

  get studRelatedEvents$(): Observable<IReferenceData[]> {
    return this.load('StudRelatedEvents');
  }

  get eiOutreachToFrom$(): Observable<IReferenceData[]> {
    return this.load('EIOutreachToFrom');
  }

  get schoolKind$(): Observable<IReferenceData[]> {
    return this.load('SchoolKind');
  }

  get programCode$(): Observable<IReferenceData[]> {
    return this.load('ProgramCode');
  }

  get classType$(): Observable<IReferenceData[]> {
    return this.load('ClassType');
  }

  get overallRatio$(): Observable<IReferenceData[]> {
    return this.load('OveralRatio');
  }

  get seRatio$(): Observable<IReferenceData[]> {
    return this.load('SERatio');
  }

  get varianceStatus$(): Observable<IReferenceData[]> {
    return this.load('VarianceStatus');
  }

  get monthYear$(): Observable<IReferenceData[]> {
    return this.load('MonthYear');
  }

  get(keys: string[]): Observable<Record<string, IReferenceData[]>> {
    const sources: Observable<readonly [string, IReferenceData[]]>[] = keys.map(
      (key: string) =>
        this.load(key).pipe(
          map((data: IReferenceData[]) => [key, data] as const)
        )
    );

    return forkJoin(sources).pipe(
      map((entries: (readonly [string, IReferenceData[]])[]) =>
        Object.fromEntries(entries)
      )
    );
  }
}
